import { Component, ViewChild, ElementRef, Input, Output, EventEmitter, signal, input } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { Subject, Subscription } from "rxjs";
import { DateTime } from "luxon";
import { LegendPosition } from "@swimlane/ngx-charts";
import { MatDialog } from "@angular/material/dialog";
import { MatSort } from "@angular/material/sort";
import { Pair } from "../../obj/Pair";
import { AbstractBuyerComponent } from "../abstract/buyer.component";
import { DatePipe } from "@angular/common";
import { FormatTimePipe } from "../../format-time.pipe";
import { CurrencyPipe } from "@angular/common";
import { Account } from "../../entity/Account";
import { KilowantContract } from "../../entity/KilowantContract";
import { BuyOffer } from "../../entity/BuyOffer";
import { SellOffer } from "../../entity/SellOffer";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { User } from "../../entity/User";
import { RateClass } from "../../entity/RateClass";
import { ChartData } from "../../obj/ChartData";
import { OffersData } from "../../obj/OffersData";
import { ChartExtension } from "../../obj/ChartExtension";
import { ChartCallout } from "../../obj/ChartCallout";
import { ChartElement } from "../../obj/ChartElement";
import { RateData } from "../../obj/RateData";
import { CurrentContractData } from "../../obj/CurrentContractData";
import { OwlOptions, SlidesOutputData, CarouselModule } from "ngx-owl-carousel-o";
import { DiffBenchmark } from "../../obj/DiffBenchmark";
import { KilowantBarHomeAcceptOfferChartComponent } from "../chart/kilowant-charts-home-accept-offer.component";
import { Serie } from "../../obj/Serie";

@Component({
    selector: "kilowant-user-home-details",
    templateUrl: "../../../html/incs/user-home-details.html",
    imports: [CarouselModule, KilowantBarHomeAcceptOfferChartComponent]
})
export class UserHomeDetailsComponent extends AbstractBuyerComponent {
    @Input("client") client: User | null;
    account = input<Account | null>(null); // converted to signal input
    @Input("selectedOffer") selectedOffer: OffersData;
    @Input("benchmarkValue") benchmarkValue: number | null = null;
    @Input("diffText") diffText: string;
    @Input("diff") diff: number;
    @Input() restartNeeded: Subject<boolean>;
    @Output("loaded") loaded = new EventEmitter<boolean>();
    @Output("outprice") outprice = new EventEmitter<number>();
    @Output("acceptOffer") acceptOffer = new EventEmitter<boolean>();
    @Output("changeDataset") changeDatasetEmitter = new EventEmitter<DiffBenchmark>();

    selectSort: MatSort;
    sort: MatSort;
    @ViewChild("slider", { static: false }) slider: any;
    @ViewChild("matSortOffers") set matSortOffers(sort: MatSort) {
        this.sort = sort;
    }
    @ViewChild("matSortSelectedOffers") set matSortSelectedOffers(sort: MatSort) {
        this.selectSort = sort;
    }
    @ViewChild("innerBody") bodyElement: ElementRef;
    @ViewChild("offersInfo") offersInfoElement: ElementRef;

    rateClasses: RateClass[] = [];
    step: number = 1;
    step2: number = 2;
    step3: number = 3;
    latestUtilityDate = signal<string | null>(null);
    latestCommunityDate = signal<string | null>(null);
    proposedBuyOfferPrice: number = 0;
    currentPosition = signal<number>(-1);
    positionOfNowSlide = signal<number>(0);
    positionOfFirstSellOfferSlide: number = 0;
    initiated: boolean = false;
    totalAllOffers: number = 0;
    allOffers: OffersData[] = [];

    suppliedByName: string;
    suppliedByPrice: number;
    suppliedByEndDate: string | null;

    countDown: Subscription;
    tick: number = 1000;
    sliderData: ChartData[];
    sliderExtension: ChartExtension[];
    displayedColumns: string[] = ["id", "Price", "TermFrom", "TermPeriod", "Vs30DMA", "Vs90DMA"];
    displayedAccountColumns: string[] = ["id", "Nickname", "RateClass"];

    currentCost = signal<CurrentContractData | null>(null);

    view = signal<[number, number]>([360, 300]);
    legend = signal<boolean>(true);
    legendTitle = signal<string>("");
    legendPosition = signal<LegendPosition>(LegendPosition.Below);
    showLabels: boolean = true;
    animations: boolean = true;
    xAxis = signal<boolean>(true);
    yAxis = signal<boolean>(true);
    showYAxisLabel = signal<boolean>(false);
    showXAxisLabel = signal<boolean>(false);
    xAxisLabel = signal<string>("Year");
    yAxisLabel = signal<string>("");
    colorScheme = signal<any>({ domain: ["#34A853", "#A0A0A0"], name: "kilowant", selectable: true, group: null! });
    viewOffersType: string = "default";
    viewSelectedOffersType: string = "all_3_6";
    viewSelectedOffersStartDate: Date | null = null;
    allOffersStartDates: Date[] = [];
    viewAccountsType: string = "all";
    showTopCard: boolean = true;
    initialScreen: boolean = false;

    availableEarliest: boolean = false;
    availablePendingAcceptance: boolean = false;
    availableSellOffer: boolean = false;
    availableBuyOffer: boolean = false;

    integratedButtonSelected = signal<number>(0);

    sliderOptions = signal<OwlOptions>({
        loop: false,
        nav: true,
        items: 1,
        dots: false,
        navText: ["", ""],
        autoplay: false,
        smartSpeed: 1000,
        responsive: {
            0: { items: 1 },
            600: { items: 2 },
            1000: { items: 1 }
        }
    });
    activeSlides: SlidesOutputData;
    resetAccount: boolean = false;
    hideOffersBlock: boolean = false;
    sortInitialised: boolean = false;
    selectSortInitialised: boolean = false;

    initGoToSlideContractId: number = -1;
    initGoToCurrentSlide: boolean = false;

    selectedDatasets = signal<number[]>([]);

    constructor(
        router: Router,
        route: ActivatedRoute,
        dialog: MatDialog,
        protected datepipe: DatePipe,
        protected currencypipe: CurrencyPipe,
        protected formattimepipe: FormatTimePipe,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService
    ) { super(router, route, dialog, authService, analyticsService, generalService); }

    ngOnInit(): void {
        this.initGoToSlideContractId = this.selectedOffer.offer.id;
        this.generalService.switchSelectedSellOffer(this.selectedOffer);
        this.start();
        this.restartNeeded.subscribe(v => {
            this.generateChart();
        });
    }

    start() {
        var promiseBuyOfferProposed = this.generalService.getBuyOfferProposedPrice(this.account()!);
        var promiseLatestUtilityDate = this.generalService.getAccountGetLatestUtilityDate(this.account()!.id);
        var promiseCurrentCosts = this.generalService.getAccountCurrentCosts(this.account()!.id);
        Promise.all([promiseBuyOfferProposed, promiseLatestUtilityDate, promiseCurrentCosts]).then((data) => {
            this.hideOffersBlock = false;
            this.resetAccount = true;
            this.proposedBuyOfferPrice = data[0];
            if (data[1].key != null) {
                this.latestUtilityDate.set(this.datepipe.transform(data[1].key, "MMM. y"));
            } else {
                this.latestUtilityDate.set(null);
            }
            if (data[1].value != null) {
                this.latestCommunityDate.set(this.datepipe.transform(data[1].value, "MMM. y"));
            } else {
                this.latestCommunityDate.set(null);
            }
            this.currentCost.set(data[2][0]);
            this.generateChart();
        });
    }

    getMinimalMarketPrice(sliderRecord: ChartData): number {
        var minMarketPrice = 0;
        for (var i = 0; i < sliderRecord.rates.length; i++) {
            for (var x = 0; x < sliderRecord.rates[i].series.length; x++) {
                if (minMarketPrice == 0 || Number(sliderRecord.rates[i].series[x].value) < minMarketPrice) {
                    minMarketPrice = Number(sliderRecord.rates[i].series[x].value);
                }
            }
        }
        return minMarketPrice;
    }

    getShift(sliderRecord: ChartData, val: Date | null, isLeft: boolean): number {
        if (val == null) {
            return 0;
        }
        if (isLeft && sliderRecord.ratesFrom != null) {
            var startChart = DateTime.fromJSDate(sliderRecord.ratesFrom);
            startChart = startChart.set({ day: 1, hour: 0, minute: 0, second: 0, millisecond: 0 });
            var shiftedVal = DateTime.fromJSDate(val);
            if (startChart < shiftedVal) {
                return 0;
            }
            return this.getDiffPeriodicityWise(shiftedVal, startChart, sliderRecord.ratesPeriodicity);
        }
        if (sliderRecord.ratesTo != null) {
            var endChart = DateTime.fromJSDate(sliderRecord.ratesTo);
            var shiftedVal = DateTime.fromJSDate(val);
            if (this.getDiffPeriodicityWise(endChart, shiftedVal, sliderRecord.ratesPeriodicity) < 0) {
                return 0;
            }
            return this.getDiffPeriodicityWise(endChart, shiftedVal, sliderRecord.ratesPeriodicity);
        }
        return 0;
    }

    getDiffPeriodicityWise(fromDate: DateTime, toDate: DateTime, periodicity: string): number {
        if (periodicity == null || periodicity == "" || periodicity == "monthly") {
            return Math.round(fromDate.diff(toDate, ["months"])["months"]);
        }
        if (periodicity == "quarterly") {
            return Math.round(fromDate.diff(toDate, ["months"])["months"] / 3);
        }
        if (periodicity == "yearly") {
            return Math.round(fromDate.diff(toDate, ["years"])["years"]);
        }
        return 0;
    }

    isBuyOffer(offer: any) {
        return offer instanceof BuyOffer;
    }

    isKilowantContract(offer: any) {
        return offer instanceof KilowantContract;
    }

    getPeriod(offer: any): number {
        if (offer == null) {
            return 0;
        }
        if (this.isKilowantContract(offer)) {
            return this.getDatesPeriod(offer.termFrom, offer.termTo);
        } else {
            return offer.termPeriod;
        }
    }

    getDatesPeriod(termFromD: Date | null, termToD: Date | null): number {
        if (termFromD == null || termToD == null) {
            return 0;
        }
        var termFrom = DateTime.fromJSDate(termFromD);
        var termTo = DateTime.fromJSDate(termToD);
        var diff = termTo.diff(termFrom, ["months"]);
        return Math.floor(diff["months"]);
    }

    isOfferTitle(i: number): boolean {
        let pos = (this.sliderExtension[i].mainCallout?.relatedSlidePos == null) ? i : this.sliderExtension[i].mainCallout?.relatedSlidePos;
        if (pos != null) {
            var topNum = this.generalService.topOfferPosition(this.sliderData[pos].selectedOfferPriceOfferId);
            if (topNum > 0) {
                return true;
            } else {
                return false;
            }
        }
        return false;
    }

    generateChart(): void {
        if (this.account() == null) {
            return;
        }
        this.generalService.getContractRateListPerAccount(this.account()!, (<SellOffer>this.selectedOffer.offer).termFrom, this.getTermTo((<SellOffer>this.selectedOffer.offer).termFrom, (<SellOffer>this.selectedOffer.offer).termPeriod)).then((res: any) => {
            this.sliderData = res;
            this.view.update(v => [this.bodyElement.nativeElement.offsetWidth, v[1]]);

            for (let i = 0; i < this.sliderData.length; i++) {
                if (this.sliderData[i].isCurrent) {
                    this.positionOfNowSlide.set(i);
                }
                if (this.sliderData[i].hasSellOffer(<SellOffer>this.selectedOffer.offer)) {
                    this.positionOfFirstSellOfferSlide = i;
                    this.sliderOptions.update(opts => ({ ...opts, startPosition: i }));
                    this.currentPosition.set(i);
                    break;
                }
            }
            this.sliderExtension = [];
            for (let s = 0; s < this.sliderData.length; s++) {
                this.generateCallout(s);
            }
            this.selectOffer(this.selectedOffer);
            if (this.sliderData.length > 0) {
                if (this.account()!.isCommunity) {
                    this.changeDataset(2);
                } else {
                    this.changeDataset(1);
                }
            }
            this._addKilowantBar();
            this.initiated = false;
            this.goToFirstSellOfferSlide();
            this.resetLegend();
            this.loaded.emit(true);
        });
    }

    generateCallout(i: number): void {
        var nextSellOfferPos = this.getNextOffer(i, true);
        var nextBuyOfferPos = this.getNextOffer(i, false);
        let chartExtension: ChartExtension;
        if (this.sliderData[i].isPast()) {
            chartExtension = this._generateCalloutForPast(i, nextSellOfferPos, nextBuyOfferPos);
        } else {
            chartExtension = this._generateCalloutForPresentFuture(i, nextSellOfferPos, nextBuyOfferPos);
        }
        this.sliderExtension.push(chartExtension);
    }

    _generateCalloutForPast(i: number, nextSellOfferPos: number | null, nextBuyOfferPos: number | null): ChartExtension {
        let chartExtension = new ChartExtension();
        this._generateMainCalloutDetailsForSellOffer(i);
        chartExtension.mainCallout = new ChartCallout();
        chartExtension.mainCallout.isOffer = false;
        chartExtension.mainCallout.title = "YOUR CONTRACT RATE WAS";
        chartExtension.mainCallout.color = "red";
        chartExtension.mainCallout.details = this._formatPrice(this.sliderData[i].contractPrice) + " from " + this.datepipe.transform(this.sliderData[i].termFrom, "M/yy") + " to " + this.datepipe.transform(this.sliderData[i].termTo, "M/yy") + " (" + this.getDatesPeriod(this.sliderData[i].termFrom, this.sliderData[i].termTo) + " mths)";
        if (this.sliderData[i].diffCommunity < 0) {
            chartExtension.mainCallout.details = chartExtension.mainCallout.details + "<br/>" + this.positive(this.sliderData[i].diffCommunity) + "% savings vs. your alternative community energy rate";
        }
        if (this.sliderData[i].diffUtility < 0) {
            chartExtension.mainCallout.details = chartExtension.mainCallout.details + "<br/>" + this.positive(this.sliderData[i].diffUtility) + "% savings vs. your alternative RI Energy supply rate";
        }
        if (this.sliderData[i].diffContract < 0) {
            chartExtension.mainCallout.details = chartExtension.mainCallout.details + "<br/>" + this.positive(this.sliderData[i].diffContract) + "% savings vs. your prior supply price plan rate";
        }

        chartExtension.line1 = new ChartElement();
        chartExtension.line1.title = "Price Plan Rate";
        chartExtension.line1.color = "#EA4336EE";
        chartExtension.line1.val = this.sliderData[i].contractPrice;
        chartExtension.line1.hasCallout = false;
        chartExtension.line1.leftShift = this.getShift(this.sliderData[i], this.sliderData[i].termFrom, true);
        chartExtension.line1.rightShift = this.getShift(this.sliderData[i], this.sliderData[i].termTo, false);
        chartExtension.mainCallout.relatedChartElement = chartExtension.line1;
        chartExtension.line1.infoText = "Guaranteed fixed-rate";
        if (this.sliderData[i].diffCommunity != 0 && !this.sliderData[i].hasMissingRates() && this.sliderData[i].diffCommunity < 0) {
            chartExtension.line1.infoText = this.positive(this.sliderData[i].diffCommunity) + "% savings off your alternative community energy supply rate";
        }
        if (this.sliderData[i].diffUtility != 0 && !this.sliderData[i].hasMissingRates() && this.sliderData[i].diffUtility < 0) {
            chartExtension.line1.infoText = this.positive(this.sliderData[i].diffUtility) + "% savings off your alternative RI Energy supply rate";
        }

        if (nextSellOfferPos != null) {
            chartExtension.rightText1 = new ChartElement();
            chartExtension.rightText1.title = "Kilowant";
            chartExtension.rightText1.color = "#d2e3fc";
            chartExtension.rightText1.val = this.selectedOffer.offer.priceForBuyer();
            chartExtension.rightText1.hasCallout = true;
            chartExtension.rightText1.relatedSlidePos = nextSellOfferPos;
            var t = this;
            var x = nextSellOfferPos;
            chartExtension.rightText1.funcLink = function () {
                t.goToSlide(x);
            };
            chartExtension.mainCallout.relatedChartElement = chartExtension.rightText1;
        }
        return chartExtension;
    }

    _generateCalloutForPresentFuture(i: number, nextSellOfferPos: number | null, nextBuyOfferPos: number | null): ChartExtension {
        let chartExtension = new ChartExtension();
        this._generateMainCalloutDetailsForSellOffer(i);
        if (i == this.positionOfFirstSellOfferSlide) {
            chartExtension.mainCallout = new ChartCallout();
            chartExtension.mainCallout.isOffer = true;
            this.sliderData[i].selectedOfferPriceValue = this.selectedOffer.offer.priceForBuyer();
            this.sliderData[i].selectedOfferPricePeriod = new Pair((<SellOffer>this.selectedOffer.offer).termFrom, this.getTermTo((<SellOffer>this.selectedOffer.offer).termFrom, ((<SellOffer>this.selectedOffer.offer)).termPeriod));
            this.sliderData[i].selectedOfferPriceOfferId = this.selectedOffer.offer.id;

            chartExtension.mainCallout.offerTitle = this._generateOfferTitle(i);
            chartExtension.mainCallout.offerId = this.sliderData[i].selectedOfferPriceOfferId;

            chartExtension.mainCallout.title = "<b>1</b> TRENDING OFFER";
            chartExtension.mainCallout.color = "";
            chartExtension.line2 = new ChartElement();
            chartExtension.line2.title = "Kilowant";
            chartExtension.line2.color = "#4286f4ff";
            chartExtension.line2.val = this.sliderData[i].selectedOfferPriceValue;
            chartExtension.line2.hasCallout = true;
            chartExtension.line2.leftShift = this.getShift(this.sliderData[i], this.sliderData[i].selectedOfferPricePeriod.key, true);
            chartExtension.line2.rightShift = this.getShift(this.sliderData[i], this.sliderData[i].selectedOfferPricePeriod.value, false);

            if (this.account()) {
                let earliestOfferPriceOfferId = this.sliderData[i].selectedOfferPriceOfferId;
                let accountIdForURL = this.account()!.id;
                let routerLink = this.router;
                chartExtension.line2.funcLink = function () {
                    routerLink.navigate(["sell-offer/details/" + earliestOfferPriceOfferId + "/" + accountIdForURL + "/"]);
                };
            }
            chartExtension.mainCallout.relatedChartElement = chartExtension.line2;
        } else if (this.sliderData[i].contractPrice > 0) {
            chartExtension.mainCallout = new ChartCallout();
            chartExtension.mainCallout.isOffer = false;
            chartExtension.mainCallout.title = "YOUR CONTRACT RATE IS";
            chartExtension.mainCallout.color = "red";
            chartExtension.mainCallout.details = this._formatPrice(this.sliderData[i].contractPrice) + " from " + this.datepipe.transform(this.sliderData[i].termFrom, "M/yy") + " to " + this.datepipe.transform(this.sliderData[i].termTo, "M/yy") + " (" + this.getDatesPeriod(this.sliderData[i].termFrom, this.sliderData[i].termTo) + " mths)";

            if (this.sliderData[i].diffCommunity < 0) {
                chartExtension.mainCallout.details = chartExtension.mainCallout.details + "<br/>" + this.positive(this.sliderData[i].diffCommunity) + "% savings vs. your alternative community energy supply rate";
            }
            if (this.sliderData[i].diffUtility < 0) {
                chartExtension.mainCallout.details = chartExtension.mainCallout.details + "<br/>" + this.positive(this.sliderData[i].diffUtility) + "% savings vs. your alternative RI Energy supply rate";
            }
            if (this.sliderData[i].diffContract < 0) {
                chartExtension.mainCallout.details = chartExtension.mainCallout.details + "<br/>" + this.positive(this.sliderData[i].diffContract) + "% savings vs. your prior supply price plan rate";
            }
            chartExtension.line1 = new ChartElement();
            chartExtension.line1.title = "Price Plan Rate";
            chartExtension.line1.color = "#EA4336FF";
            chartExtension.line1.val = this.sliderData[i].contractPrice;
            chartExtension.line1.hasCallout = false;
            chartExtension.line1.leftShift = this.getShift(this.sliderData[i], this.sliderData[i].termFrom, true);
            chartExtension.line1.rightShift = this.getShift(this.sliderData[i], this.sliderData[i].termTo, false);

            if (this.sliderData[i].isKilowantContract) {
                var thisData = this.sliderData[i];
                var routerLink = this.router;
                chartExtension.line1.funcLink = function () {
                    routerLink.navigate(["/view-priceplan/" + thisData.contractId]);
                };
            }

            if (nextSellOfferPos != null) {
                chartExtension.rightText1 = new ChartElement();
                chartExtension.rightText1.title = "Kilowant";
                chartExtension.rightText1.color = "#d2e3fc";
                chartExtension.rightText1.val = this.selectedOffer.offer.priceForBuyer();
                chartExtension.rightText1.hasCallout = true;
                chartExtension.rightText1.relatedSlidePos = nextSellOfferPos;
                var t = this;
                var x = nextSellOfferPos;
                chartExtension.rightText1.funcLink = function () {
                    t.goToSlide(x);
                };
                chartExtension.mainCallout.relatedChartElement = chartExtension.rightText1;
            }
        } else if (nextSellOfferPos) {
            if (this.sliderData[i].contractPrice > 0) {
                chartExtension.line1 = new ChartElement();
                chartExtension.line1.title = "Price Plan Rate";
                chartExtension.line1.color = "#EA4336";
                chartExtension.line1.val = this.sliderData[i].contractPrice;
                chartExtension.line1.hasCallout = false;
                chartExtension.line1.leftShift = this.getShift(this.sliderData[i], this.sliderData[i].termFrom, true);
                chartExtension.line1.rightShift = this.getShift(this.sliderData[i], this.sliderData[i].termTo, false);
            }
            chartExtension.mainCallout = new ChartCallout();
            chartExtension.mainCallout.isOffer = true;
            this.sliderData[nextSellOfferPos].selectedOfferPriceValue = this.sliderData[nextSellOfferPos].earliestOfferPriceValue;
            this.sliderData[nextSellOfferPos].selectedOfferPricePeriod = this.sliderData[nextSellOfferPos].earliestOfferPricePeriod;
            this.sliderData[nextSellOfferPos].selectedOfferPriceOfferId = this.sliderData[nextSellOfferPos].earliestOfferPriceOfferId;

            chartExtension.mainCallout.offerTitle = this._generateOfferTitle(nextSellOfferPos);
            chartExtension.mainCallout.offerId = this.sliderData[nextSellOfferPos].selectedOfferPriceOfferId;

            chartExtension.mainCallout.title = "<b>1</b> TRENDING OFFER";
            chartExtension.mainCallout.relatedSlidePos = nextSellOfferPos;
            chartExtension.mainCallout.color = "";
            this.sliderData[i].showCounterButton = this.sliderData[nextSellOfferPos].showCounterButton;

            chartExtension.rightText1 = new ChartElement();
            chartExtension.rightText1.title = "Kilowant";
            chartExtension.rightText1.color = "#d2e3fc";
            chartExtension.rightText1.val = this.sliderData[nextSellOfferPos]!.selectedOfferPriceValue;
            chartExtension.rightText1.hasCallout = true;
            chartExtension.rightText1.relatedSlidePos = nextSellOfferPos;
            var t = this;
            var x = nextSellOfferPos;
            chartExtension.rightText1.funcLink = function () {
                t.goToSlide(x);
            };
            chartExtension.mainCallout.relatedChartElement = chartExtension.rightText1;
        }

        if (((this.sliderData[i].buyOfferPriceValue > 0 && (this.sliderData[i].diffUtility > 0 || !chartExtension.mainCallout)) || nextBuyOfferPos)) {
            chartExtension.mainCallout = new ChartCallout();
            chartExtension.mainCallout.isOffer = false;
            chartExtension.mainCallout.color = "yellow-back";

            var infoPos = (this.sliderData[i].buyOfferPriceValue > 0) ? i : nextBuyOfferPos;
            if (!infoPos) {
                infoPos = i;
            }
            var p = this.sliderData[infoPos].buyOfferPriceValue;
            var s = DateTime.fromJSDate(this.sliderData[infoPos].buyOfferPricePeriod.key);
            var e = DateTime.fromJSDate(this.sliderData[infoPos].buyOfferPricePeriod.value);
            var bid = this.sliderData[infoPos].buyOfferId;
            var t = this;

            chartExtension.mainCallout.title = "UPDATE BUYER OFFER";
            chartExtension.mainCallout.details = "You have an open buyer offer @ " + this.currencypipe.transform(this.sliderData[infoPos].buyOfferPriceValue, "USD", "symbol", "1.5-5") + "/kWh for " + this.getTermPeriod(this.sliderData[infoPos].buyOfferPricePeriod.key, this.sliderData[infoPos].buyOfferPricePeriod.value) + " months starting " + this.datepipe.transform(this.sliderData[infoPos].buyOfferPricePeriod.key, "shortDate");
            this.sliderData[i].showCounterButton = false;
            this.sliderData[i].showUpdateButton = true;
            if (chartExtension.rightText1 != null) {
                chartExtension.rightText1.title = "Suggested Offer";
            }
            if (chartExtension.line2 != null) {
                chartExtension.line2.title = "Suggested Offer";
            }
        } else if (!chartExtension.mainCallout) {
            // NO SELLER OFFERS, NO BUYER OFFERS, NO CONTRACT
            chartExtension.mainCallout = new ChartCallout();
            chartExtension.mainCallout.isOffer = false;
            chartExtension.mainCallout.color = "yellow-back";
            var nextDateInfo = this.datepipe.transform(this.sliderData[i].termFrom, "shortDate");
            if (this.sliderData[i].contractPrice > 0) {
                var nextDateInfo = this.datepipe.transform(this.sliderData[i].termTo, "shortDate");
            }
            chartExtension.mainCallout.title = "NO AVAILABLE SELLER OFFERS";
            chartExtension.mainCallout.details = "There are currently no seller offers with a service start of " + nextDateInfo + ". However, you can submit a buyer offer with a target price and term of your choosing. Sellers will have the option to accept your buyer offer.";
            this.sliderData[i].showSubmitButton = true;
            var thisData = this.sliderData[i];
            var routerLink = this.router;
        }
        return chartExtension;
    }

    _generateMainCalloutDetailsForSellOffer(pos: number): void {
        this.sliderData[pos].showCounterButton = false;
        if (this.sliderData[pos].selectedOfferPriceValue == null) {
            return;
        }
        if (this.diff > 0) {
            return;
        }
        this.sliderData[pos].showCounterButton = true;
        return;
    }

    getNextOffer(pos: number, isSellOffer: boolean): number | null {
        if (isSellOffer) {
            return this.positionOfFirstSellOfferSlide;
        }
        while (pos < this.sliderData.length) {
            if (!isSellOffer && this.sliderData[pos].buyOfferPriceValue > 0) {
                return pos;
            }
            pos++;
        }
        return null;
    }

    goToNowSlide(): void {
        if (this.slider != null) {
            this.slider.to("" + this.positionOfNowSlide());
        } else {
            this.sliderOptions.update(opts => ({ ...opts, startPosition: this.positionOfNowSlide() }));
        }
    }

    goToSlideWithId(contractId: number): void {
        for (let i: number = 0; i < this.sliderData.length; i++) {
            if (this.sliderData[i].contractId == contractId) {
                if (this.slider != null) {
                    this.goToSlide(i);
                } else {
                    this.sliderOptions.update(opts => ({ ...opts, startPosition: i }));
                }
                return;
            }
        }
    }

    goToFirstSellOfferSlide(): void {
        if (this.slider != null) {
            this.slider.to("" + this.positionOfFirstSellOfferSlide);
        }
    }

    goToSlide(i: number): void {
        if (i < this.sliderData.length) {
            this.slider.to("" + i);
        }
    }

    goToPrev(): void {
        if (this.currentPosition() >= 0) {
            this.slider.to("" + (this.currentPosition() - 1));
        }
    }

    goToNext(): void {
        if (this.currentPosition() < this.sliderData.length) {
            this.slider.to("" + (this.currentPosition() + 1));
        }
    }

    sliderChange(data: SlidesOutputData): void {
        if (this.initiated) {
            var newPosition = Number(data.startPosition);
            if (newPosition != this.currentPosition()) {
                this.currentPosition.set(newPosition);
            }
        } else {
            this.initiated = true;
        }
    }

    getTermTo(termFrom: Date, termPeriod: number): Date {
        var d = new Date(termFrom);
        return new Date(d.setMonth(d.getMonth() + termPeriod));
    }

    getClassPerOfferInList(element: any, plusSelected: boolean): string {
        if (this.isKilowantContract(element)) {
            return "highlight-red" + ((plusSelected) ? ' selected' : '');
        }
        if (this.sliderData == null) {
            return ((plusSelected) ? ' selected' : '');
        }
        for (var i = 0; i < this.sliderData.length; i++) {
            if ((this.sliderData != null && this.sliderData[i] != null && this.sliderData[i].earliestOfferPriceOfferId != 0 && this.sliderData[i].earliestOfferPriceOfferId == element.id)) {
                return "highlight-blue" + ((plusSelected) ? ' selected' : '');
            }
        }
        return ((plusSelected) ? ' selected' : '');
    }

    resetLegend(): void {
        this.availableEarliest = false;
        this.availablePendingAcceptance = false;
        this.availableSellOffer = false;
        this.availableBuyOffer = false;
        for (var x = 0; x < this.allOffers.length; x++) {
            if (this.isKilowantContract(this.allOffers[x].offer)) {
                this.availablePendingAcceptance = true;
            } else if (this.isBuyOffer(this.allOffers[x].offer)) {
                this.availableBuyOffer = true;
            } else if (this.getClassPerOfferInList(this.allOffers[x].offer, false) == "highlight-blue") {
                this.availableEarliest = true;
            } else {
                this.availableSellOffer = true;
            }
        }
    }

    selectOffer(element: OffersData): void {
        let pos: number;
        pos = this.positionOfFirstSellOfferSlide;
        if (element == null || this.currentPosition() < 0) {
            return;
        }

        this.sliderData[pos].selectedOfferPriceValue = element.offer.priceForBuyer();
        this.sliderData[pos].selectedOfferPriceOfferId = element.offer.id;
        this.sliderData[pos].selectedOfferPricePeriod = new Pair(element.offer.termFrom, this.getTermTo(element.offer.termFrom, (<SellOffer>element.offer).termPeriod));

        if (this.currentPosition() != pos) {
            this.sliderData[this.currentPosition()].selectedOfferPriceValue = element.offer.priceForBuyer();
            this.sliderData[this.currentPosition()].selectedOfferPriceOfferId = element.offer.id;
            this.sliderData[this.currentPosition()].selectedOfferPricePeriod = new Pair(element.offer.termFrom, this.getTermTo(element.offer.termFrom, (<SellOffer>element.offer).termPeriod));
        }

        if (this.getDatesPeriod(this.sliderData[pos].ratesTo!, this.getTermTo(element.offer.termFrom, (<SellOffer>element.offer).termPeriod))) {
            if (pos != this.currentPosition()) {
                this.sliderExtension[this.currentPosition()].rightText1!.val = this.sliderData[pos].selectedOfferPriceValue;
            } else {
                for (let i = 0; i < pos; i++) {
                    if (this.sliderExtension[i].mainCallout != null && this.sliderExtension[i].mainCallout!.relatedChartElement != null && this.sliderExtension[i].mainCallout!.relatedChartElement!.relatedSlidePos == pos) {
                        this.sliderExtension[i].rightText1!.val = this.sliderData[pos].selectedOfferPriceValue;
                    }
                }
            }
            this.sliderExtension[pos].line2!.val = this.sliderData[pos].selectedOfferPriceValue;
            this.sliderExtension[pos].line2!.leftShift = this.getShift(this.sliderData[pos], this.sliderData[pos].selectedOfferPricePeriod.key, true);
            this.sliderExtension[pos].line2!.rightShift = this.getShift(this.sliderData[pos], this.sliderData[pos].selectedOfferPricePeriod.value, false);

            if (pos == this.currentPosition()) {
                for (let i = 0; i < pos; i++) {
                    if (this.sliderExtension[i].mainCallout != null && this.sliderExtension[i].mainCallout!.relatedChartElement != null && this.sliderExtension[i].mainCallout!.relatedChartElement!.relatedSlidePos == pos) {
                        this.sliderData[i].showCounterButton = this.sliderData[pos].showCounterButton;
                    }
                }
            }
            this.sliderData[this.currentPosition()].showCounterButton = this.sliderData[pos].showCounterButton;
        } else {
            if (pos != this.currentPosition()) {
                this.sliderExtension[this.currentPosition()].rightText1!.val = this.sliderData[pos].selectedOfferPriceValue;
            }
            this.sliderExtension[pos].line2!.val = this.sliderData[pos].selectedOfferPriceValue;
            this.sliderExtension[pos].line2!.leftShift = this.getShift(this.sliderData[pos], this.sliderData[pos].selectedOfferPricePeriod.key, true);
            this.sliderExtension[pos].line2!.rightShift = this.getShift(this.sliderData[pos], this.sliderData[pos].selectedOfferPricePeriod.value, false);

            if (pos == this.currentPosition()) {
                for (let i = 0; i < pos; i++) {
                    if (this.sliderExtension[i].mainCallout != null && this.sliderExtension[i].mainCallout!.relatedChartElement != null && this.sliderExtension[i].mainCallout!.relatedChartElement!.relatedSlidePos == pos) {
                        this.sliderData[i].showCounterButton = this.sliderData[pos].showCounterButton;
                    }
                }
            }
            this.sliderData[this.currentPosition()].showCounterButton = this.sliderData[pos].showCounterButton;
        }
    }

    getRateClassAbbr(rateClass: string): string {
        return this.generalService.getRateClassAbbr(rateClass);
    }

    getRelatedPos(pos: number): number {
        if (this.sliderExtension[pos].mainCallout != null && this.sliderExtension[pos].mainCallout!.relatedChartElement != null && this.sliderExtension[pos].mainCallout!.relatedChartElement!.relatedSlidePos != null) {
            pos = this.sliderExtension[pos].mainCallout!.relatedChartElement!.relatedSlidePos!;
        }
        return pos;
    }

    gotoSellOffer(pos: number): void {
        this.acceptOffer.emit(true);
    }

    gotoBuyOffer(pos: number): void {
        if (this.sliderExtension[pos].mainCallout != null && this.sliderExtension[pos].mainCallout!.relatedChartElement != null && this.sliderExtension[pos].mainCallout!.relatedChartElement!.relatedSlidePos != null) {
            pos = this.sliderExtension[pos].mainCallout!.relatedChartElement!.relatedSlidePos!;
        }
        let offerId = this.sliderData[pos].selectedOfferPriceOfferId;
        this.router.navigate(["/buy-offer/add"], { queryParams: { benchmarkOffer: offerId, accounts: ((this.account() == null) ? "" : this.account()!.id) } });
    }

    gotoEditBuyOffer(pos: number): void {
        if (this.sliderExtension[pos].mainCallout != null && this.sliderExtension[pos].mainCallout!.relatedChartElement != null && this.sliderExtension[pos].mainCallout!.relatedChartElement!.relatedSlidePos != null) {
            pos = this.sliderExtension[pos].mainCallout!.relatedChartElement!.relatedSlidePos!;
        }
        let offerId = this.sliderData[pos].buyOfferId;
        this.router.navigate(["/buy-offer/edit/" + offerId]);
    }

    intergatedSelection(pos: number, integratedNumber: number): void {
        if (this.integratedButtonSelected() != integratedNumber) {
            this.integratedButtonSelected.set(integratedNumber);
            if (this.sliderExtension[pos].line2 != null && this.sliderExtension[pos].line2!.val > 0) {
                this.outprice.emit((integratedNumber == 0) ? this.sliderExtension[pos].line2!.val : this.sliderExtension[pos].line2!.val * 0.9);
            }
            if (this.sliderExtension[pos].rightText1 != null && this.sliderExtension[pos].rightText1!.val > 0) {
                this.outprice.emit((integratedNumber == 0) ? this.sliderExtension[pos].rightText1!.val : this.sliderExtension[pos].rightText1!.val * 0.9);
            }
            return;
        }
        if (integratedNumber == 0) {
            this.gotoSellOffer(pos);
            return;
        }
        this.gotoBuyOffer(pos);
        return;
    }

    addAccount(): void {
        this.router.navigate(["/account/add"]);
    }

    addBuyOffer(): void {
        this.router.navigate(["/buy-offer/add"]);
    }

    editBuyOffer(buyOffer: BuyOffer): void {
        this.router.navigate(["/buy-order/details/" + buyOffer.id]);
    }

    goToBuyOffer(): void {
        this.router.navigate(["/buy-order/details/"]);
    }

    changeDataset(pos: number): void {
        if (this.selectedDatasets().includes(pos) && this.selectedDatasets().length > 1) {
            this.selectedDatasets.update(arr => arr.filter(item => item !== pos));
        } else if (!this.selectedDatasets().includes(pos)) {
            this.selectedDatasets.update(arr => [...arr, pos]);
        }
        let hasUtility = false;
        let hasCommunity = false;
        for (let i = 0; i < this.sliderData.length; i++) {
            this.sliderData[i].activeRates = [];
            for (let x = 0; x < this.sliderData[i].rates.length; x++) {
                let r = new RateData(this.sliderData[i].rates[x].name, []);
                for (let y = 0; y < this.sliderData[i].rates[x].series.length; y++) {
                    if (this.selectedDatasets().includes((y + 1))) {
                        if (this.sliderData[i].rates[x].series[y].name == "RI Utility") {
                            hasUtility = true;
                        }
                        if (this.sliderData[i].rates[x].series[y].name == "NextEra Energy") {
                            hasCommunity = true;
                        }
                        r.series.push(this.sliderData[i].rates[x].series[y]);
                    }
                }
                this.sliderData[i].activeRates.push(r);
            }
        }
        if (hasUtility && hasCommunity) {
            this.changeDatasetEmitter.next(DiffBenchmark.COMMUNITY);
        } else if (!hasUtility && hasCommunity) {
            this.changeDatasetEmitter.next(DiffBenchmark.COMMUNITY);
        } else {
            this.changeDatasetEmitter.next(DiffBenchmark.UTILITY);
        }
        this._addKilowantBar();
    }

    _generateOfferTitle(pos: number): string {
        return this.datepipe.transform(this.sliderData[pos].selectedOfferPricePeriod.key, "shortDate") + " / " + this.getTermPeriod(this.sliderData[pos].selectedOfferPricePeriod.key, this.sliderData[pos].selectedOfferPricePeriod.value) + "mnth / @" + this.currencypipe.transform(this.sliderData[pos].selectedOfferPriceValue, "USD", "symbol", "1.5-5") + "/kWh";
    }

    chartLineFunction(i: number): void {
        if (this.sliderExtension[i].line1 && this.sliderExtension[i].line1!.funcLink != null) {
            this.sliderExtension[i].line1!.funcLink();
            return;
        }
        if (this.integratedButtonSelected() == 0) {
            if (this.sliderExtension[i].line2 && this.sliderExtension[i].line2!.funcLink != null) {
                this.sliderExtension[i].line2!.funcLink();
                return;
            }
            this.gotoBuyOffer(i);
        }
    }

    _addKilowantBar(): void {
        for (let s = 0; s < this.sliderData.length; s++) {
            if (this.sliderData[s].activeRates != null) {
                for (let j = 0; j < this.sliderData[s].activeRates.length; j++) {
                    let priceVal = (this.sliderExtension[s].line1) ? this.sliderExtension[s].line1!.val : ((this.sliderExtension[s].line2) ? ((this.integratedButtonSelected() == 0) ? this.sliderExtension[s].line2!.val : this.sliderExtension[s].line2!.val * 0.9) : 0);
                    this.sliderData[s].activeRates[j].series.push(new Serie(((this.sliderExtension[s].line1) ? this.sliderExtension[s].line1?.title! : this.sliderExtension[s].line2?.title!) + " " + (new CurrencyPipe("en-US")).transform(priceVal, "USD", "symbol", "1.3-3") + "/kWh", priceVal));
                }
            }
        }
    }

    _formatPrice(price: number): string {
        return this.currencypipe.transform(price, "USD", "symbol", "1.5-5") + "/kWh";
    }
}